import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';
import Tab from '@mui/material/Tab';
import ResultCountBadge from './ResultCountBadge';
import { catOf } from './bulkOnboardingApi';
import { CircleCheck, CircleMinus, CircleX } from 'lucide-react';

const RESULT_GROUPS = [
  {
    key: 'invited',
    label: 'Invited',
    Icon: CircleCheck,
    tone: 'text-secondary-darkened',
    bg: 'bg-secondary-light'
  },
  {
    key: 'skipped',
    label: 'Skipped',
    Icon: CircleMinus,
    tone: 'text-warning-contrast',
    bg: 'bg-semantic-warning'
  },
  {
    key: 'error',
    label: 'Errors',
    Icon: CircleX,
    tone: 'text-semantic-danger',
    bg: 'bg-semantic-danger/10'
  }
];

/**
 * tabbed list of the per-row bulk invite results, grouped by outcome category.
 * @param results - per-row backend results to display
 */
export default function ResultsList({ results }) {
  const [activeGroup, setActiveGroup] = useState(RESULT_GROUPS[0].key);

  const rowsByGroup = useMemo(() => {
    const map = { invited: [], skipped: [], error: [] };
    results.forEach(r => {
      map[catOf(r)]?.push(r);
    });
    return map;
  }, [results]);

  const activeRows = rowsByGroup[activeGroup] ?? [];
  const activeGroupInfo = RESULT_GROUPS.find(g => g.key === activeGroup);

  return (
    <div className='flex flex-col gap-4 text-left'>
      <ScrollableTabs
        value={activeGroup}
        onChange={(_event, nextTab) => setActiveGroup(nextTab)}
        aria-label='Bulk invite results'
      >
        {RESULT_GROUPS.map(group => (
          <Tab
            key={group.key}
            value={group.key}
            label={
              <span className='flex items-center gap-2'>
                <span>{group.label}</span>
                <ResultCountBadge
                  count={rowsByGroup[group.key].length}
                  tone={group.tone}
                  bg={group.bg}
                />
              </span>
            }
          />
        ))}
      </ScrollableTabs>

      {activeRows.length === 0 ? (
        <p className='m-0 text-sm text-grey'>No agents in this category.</p>
      ) : (
        <ul className='m-0 flex list-none flex-col gap-1.5 p-0'>
          {activeRows.map((r, i) => {
            const { Icon, tone } = activeGroupInfo;
            return (
              <li
                key={`${r.npn ?? ''}-${r.email ?? ''}-${i}`}
                className='flex items-start gap-2 rounded-md border border-grey-lighter px-3 py-2 text-sm'
              >
                <Icon className={`mt-0.5 size-4 shrink-0 ${tone}`} aria-hidden />
                <div className='min-w-0'>
                  <span className='font-semibold text-black'>
                    {r.agentName || '—'}
                  </span>
                  {r.npn && <span className='ml-1 text-grey'>NPN {r.npn}</span>}
                  {r.email && (
                    <span className='ml-1 text-grey'>— {r.email}</span>
                  )}
                  {activeGroup !== 'invited' && r.message && (
                    <p className={`m-0 mt-0.5 text-xs ${tone}`}>{r.message}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

ResultsList.propTypes = {
  results: PropTypes.array.isRequired
};
